import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { cn } from "@/lib/utils";

interface SaveButtonProps {
  propertyId: string;
  className?: string;
  variant?: "icon" | "full";
}

export function SaveButton({ propertyId, className, variant = "icon" }: SaveButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isSaved, toggleSave, isToggling } = useSavedProperties();

  const saved = isSaved(propertyId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate("/auth", { state: { from: window.location.pathname } });
      return;
    }

    toggleSave(propertyId);
  };

  if (variant === "full") {
    return (
      <Button
        variant={saved ? "secondary" : "outline"}
        onClick={handleClick}
        disabled={isToggling}
        className={className}
      >
        <Heart
          className={cn(
            "h-4 w-4 mr-2 transition-colors",
            saved && "fill-primary text-primary"
          )}
        />
        {saved ? "Saved" : "Save Property"}
      </Button>
    );
  }

  return (
    <Button
      size="icon"
      variant="secondary"
      onClick={handleClick}
      disabled={isToggling}
      className={cn(
        "h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background",
        className
      )}
    >
      <Heart
        className={cn(
          "h-5 w-5 transition-colors",
          saved ? "fill-primary text-primary" : "text-muted-foreground"
        )}
      />
    </Button>
  );
}
